import React, { useMemo } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";

export type PaymentTrendPoint = {
  label: string;
  billed: number;
  collected: number;
  outstanding: number;
  invoices: number;
};

type PaymentSummary = {
  totalSum?: number;
  discountSum?: number;
  cardSum?: number;
  cashSum?: number;
  bankTransferSum?: number;
  dueSum?: number;
};

const CHART_SX = {
  "& .MuiChartsAxis-tickLabel": {
    fill: "#64748b",
    fontSize: "11px",
  },
  "& .MuiChartsLegend-label": {
    fill: "#475569",
    fontSize: "12px",
  },
};

const METHOD_COLORS = ["#10b981", "#6366f1", "#0ea5e9"];

function formatTrendLabel(label: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
    const date = new Date(`${label}T12:00:00`);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  const parts = label.split("-");
  if (parts.length === 2) {
    const month = Number(parts[1]);
    if (month >= 1 && month <= 12) {
      const date = new Date(Number(parts[0]), month - 1, 1);
      return date.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
    }
    return `Week ${parts[1]}`;
  }

  return label;
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex h-[260px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">
      {message}
    </div>
  );
}

function ChartPanel({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-200/40 dark:border-slate-700/80 dark:bg-slate-800/60 dark:shadow-none p-5 sm:p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
          <Icon sx={{ fontSize: 20 }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
          {subtitle ? (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}

type PaymentReportChartsProps = {
  summary: PaymentSummary;
  trend: PaymentTrendPoint[];
  loading: boolean;
};

export default function PaymentReportCharts({
  summary,
  trend,
  loading,
}: PaymentReportChartsProps) {
  const cash = Number(summary.cashSum ?? 0);
  const card = Number(summary.cardSum ?? 0);
  const transfer = Number(summary.bankTransferSum ?? 0);
  const collected = cash + card + transfer;
  const outstanding = Number(summary.dueSum ?? 0);
  const discount = Number(summary.discountSum ?? 0);
  const total = Number(summary.totalSum ?? 0);

  const paymentMethodData = useMemo(
    () =>
      [
        { id: 0, label: "Cash", value: cash, color: METHOD_COLORS[0] },
        { id: 1, label: "Card", value: card, color: METHOD_COLORS[1] },
        { id: 2, label: "Transfer", value: transfer, color: METHOD_COLORS[2] },
      ].filter((item) => item.value > 0),
    [cash, card, transfer]
  );

  const trendLabels = useMemo(
    () => trend.map((point) => formatTrendLabel(point.label)),
    [trend]
  );

  const hasTrend = trend.length > 0 && trend.some((p) => p.billed > 0 || p.collected > 0);
  const hasMethods = paymentMethodData.length > 0;
  const hasBreakdown = total > 0 || collected > 0 || outstanding > 0 || discount > 0;

  return (
    <div className="space-y-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        Charts
      </p>

      <div className="grid gap-6 xl:grid-cols-2">
        <ChartPanel
          icon={ShowChartOutlinedIcon}
          title="Collections over time"
          subtitle="Billed vs collected for the selected period"
        >
          {loading ? (
            <EmptyChart message="Loading chart…" />
          ) : !hasTrend ? (
            <EmptyChart message="No payment activity in this period." />
          ) : (
            <LineChart
              height={280}
              series={[
                {
                  id: "billed",
                  label: "Billed",
                  data: trend.map((point) => point.billed),
                  color: "#94a3b8",
                  curve: "monotoneX",
                  showMark: trend.length <= 12,
                },
                {
                  id: "collected",
                  label: "Collected",
                  data: trend.map((point) => point.collected),
                  color: "#6366f1",
                  curve: "monotoneX",
                  showMark: trend.length <= 12,
                },
              ]}
              xAxis={[
                {
                  scaleType: "point",
                  data: trendLabels,
                  tickLabelStyle: { angle: trend.length > 8 ? -35 : 0, textAnchor: trend.length > 8 ? "end" : "middle" },
                },
              ]}
              margin={{ left: 56, right: 16, top: 16, bottom: trend.length > 8 ? 56 : 36 }}
              sx={CHART_SX}
            />
          )}
        </ChartPanel>

        <ChartPanel
          icon={PieChartOutlineOutlinedIcon}
          title="Payment methods"
          subtitle="Share of collections by type"
        >
          {loading ? (
            <EmptyChart message="Loading chart…" />
          ) : !hasMethods ? (
            <EmptyChart message="No collections recorded in this period." />
          ) : (
            <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-start lg:justify-center">
              <PieChart
                series={[
                  {
                    data: paymentMethodData,
                    innerRadius: 50,
                    outerRadius: 95,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { fade: "global", highlight: "item" },
                  },
                ]}
                height={260}
                width={260}
                slotProps={{
                  legend: { hidden: true },
                }}
              />
              <div className="w-full max-w-[220px] space-y-2">
                {paymentMethodData.map((item) => {
                  const percent = collected > 0 ? (item.value / collected) * 100 : 0;
                  return (
                    <div key={item.label} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="font-medium text-slate-600 dark:text-slate-300">
                          {item.label}
                        </span>
                      </div>
                      <span className="tabular-nums text-slate-500 dark:text-slate-400">
                        {percent.toFixed(0)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ChartPanel>
      </div>

      <ChartPanel
        icon={BarChartOutlinedIcon}
        title="Revenue breakdown"
        subtitle="Collected, outstanding, and discounts"
      >
        {loading ? (
          <EmptyChart message="Loading chart…" />
        ) : !hasBreakdown ? (
          <EmptyChart message="No revenue data in this period." />
        ) : (
          <BarChart
            height={280}
            xAxis={[
              {
                scaleType: "band",
                data: ["Collected", "Outstanding", "Discounts"],
              },
            ]}
            series={[
              {
                data: [collected, outstanding, discount],
                color: "#6366f1",
                label: "Amount",
              },
            ]}
            margin={{ left: 56, right: 16, top: 16, bottom: 36 }}
            sx={CHART_SX}
            barLabel={(item) =>
              item.value != null && item.value > 0
                ? item.value.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : ""
            }
          />
        )}
      </ChartPanel>
    </div>
  );
}
